import { EvaEndpoint } from './endpoint';
import { EVA_API_VERSION, IEvaServiceDefinition } from '@springtree/eva-services-core';
import { evaServiceInterceptors } from './interceptors';
import { getServiceSettings, setServiceSetting } from './settings';
import { logger } from '@springtree/eva-sdk-core-logger';
import { getCoreSettings } from '@springtree/eva-sdk-core-settings';
// tslint:disable-next-line: import-name
import ky from 'ky-universal';
import { Options } from 'ky';
import { v4 } from 'uuid';
import md5 from 'md5';
import { stringify } from 'safe-stable-stringify';

/**
 * Constructor arguments for EVA service
 *
 * @export
 * @interface IEvaServiceArguments
 */
export interface IEvaServiceArguments {
  /**
   * Optional flag to disable interceptors
   * This is an advanced feature that will more then likely never be set
   * The EVA bootstrap requires this option for pre-bootstrap service calls
   *
   * @type {boolean}
   */
  disableInterceptors?: boolean;

  /**
   * Disables the endpoint is bootstrapped check
   * Needed for the endpoint itself to do the bootstrap calls
   *
   * @type {boolean}
   */
  disableEndpointBootstrapCheck?: boolean;
}

/**
 * The additional options to use for an EVA Service call
 * These can be used to override any defaults
 *
 * @export
 * @interface IEvaServiceCallOptions
 */
export interface IEvaServiceCallOptions {

  /**
   * Used to override the default service timeout
   *
   * @type {number}
   */
  timeout?: number;

  /**
   * The authentication token to use for this service call
   *
   * @type {string}
   */
  authenticationToken?: string;

  /**
   * A specific application token to use for this service call
   * By default the current application token from the settings will be used.
   * The setting will be updated whenever a new EVA-App-Token is returned.
   *
   * @type {string}
   */
  applicationToken?: string;

  /**
   * Each service call is provided with a unique context.
   * You can provide a value if you do not want an auto-generated one
   *
   * @type {string}
   */
  serviceContext?: string;

  /**
   * If an EVA Service is called in a chain this identifier can be added to
   * to the headers to identify the collection of services
   *
   * @type {string}
   */
  chainContext?: string;

  /**
   * Can be used by clients to set the current station ID header value
   * This should always reflect the ID from the currently used station from
   * the `ListStationsForOrganization` response. Added for legal reasons
   * Is usually set using the service settings but can be overridden per call
   * using this setting
   *
   * @type {(number)}
   */
  currentStationID?: number;

  /**
   * Can be used to set the `EVA-Requested-OrganizationUnitID` header.
   * This is used to select a specific organization unit when making calls without
   * a specific user token by ID.
   *
   * @type {number}
   */
  requestedOrganizationUnitID?: number;

  /**
   * Can be used to set the `EVA-Requested-OrganizationUnit-Query` header.
   * This is used to select a specific organization unit when making calls without
   * a specific user token using an exact match query on name and backend id.
   *
   *
   * @type {{ Name: string; BackendID: string; }}
   */
  requestedOrganizationUnitQuery?: { Name: string; BackendID: string; };

  /**
   * Can be used to set the `EVA-IDs-Mode` header which can be used to let the EVA
   * backend to use external identifiers instead of EVA identifiers for
   * things like products.
   * Is usually set using the service settings but can be overridden per call
   * using this setting
   *
   * @type {string}
   */
  backendIdMode?: string;

   /**
    * Used to populate the `EVA-IDs-BackendSystemID` header which can be sent
    * along with the `EVA-IDs-Mode` header to enable response metadata to help
    * with mapping identifiers between EVA and an external system
    *
    * @type {string}
    */
  backendIdSystemID?: string;

  /**
   * Used to populate the `EVA-Elevation-Token` header which can be used to
   * validate calls that require an additional authorization check from another
   * user
   *
   * @type {string}
   */
  elevationToken?: string;

  /**
   * Adds custom headers to the request
   *
   * @type {{[key: string]: string}}
   */
  headers?: {[key: string]: string};

  /**
   * Used to pass an outside request abort signal to the service request.
   * If supplied the `.abort()` method will no longer do function as the external
   * abort signalr will be used instead.
   */
  abortSignal?: AbortSignal;

  /**
   * Used to set the `Accept-Language` header per call.
   * Defaults to the springtree/eva-sdk-core-settings language setting if not provided.
   * Language is a locale such as 'nl_NL' or 'en_US'
   *
   * @type {string}
   */
  language?: string;
}

/**
 * A service error response for the EVA backend
 *
 * @export
 * @interface IEvaServiceError
 */
export interface IEvaServiceError {
  status: number;
  data: any;
  message: string;
}

/**
 * Helper method to get the JSON body from a fetch response
 *
 * @param {Response} response
 * @returns {(Promise<any|undefined>)}
 */
export const safeGetResponseJSON = async (response: Response): Promise<any|undefined> => {
  let responseBody;
  try {
    responseBody = await response.json();
  } catch (jsonError) {
    logger.warn('[EVA:SERVICE] Failed to extract JSON from response', jsonError)();
  }
  return responseBody;
};

/**
 * Helper method to create a stable md5 hash for a JSON object
 *
 * @param {Record<string|number, any>} jsonData
 * @returns {string}
 */
export const safeStableJSONHash = (jsonData: Record<string|number, any> = {}) => {
  try {
    const hashed = md5(
      stringify(jsonData),
    );
    return hashed;
  } catch (jsonError) {
    logger.warn('[EVA:SERVICE] Failed to hash JSON', jsonError, jsonData)();
    return '';
  }
};

/**
 * The EVA Service implementation
 *
 * @export
 * @class EvaService
 * @template REQ
 * @template RES
 */
export class EvaService<SVC extends IEvaServiceDefinition> {

  /**
   * The name of the EVA Service being called
   *
   * @private
   */
  private serviceName = 'generic';

  /**
   * The current abort controller for the fetch request
   *
   * @private
   * @type {AbortController}
   */
  private abortController?: AbortController;

  /**
   * The applied default options for the call
   *
   * @private
   * @type {Options}
   */
  private defaultOptions: Options;

  /**
   * Stops the current service call
   *
   */
  public abort() {
    this.abortController && this.abortController.abort();
  }

  /**
   * The fetch response object
   *
   * @type {(Response|undefined)}
   */
  public fetchResponse?: Response;

  /**
   * Creates an instance of EvaService
   * pre-configured with default EVA headers and options
   *
   * @param {SVC} data The service definition that will be used to make the call and receive the result
   * @param {EvaEndpoint} endpoint The bootstrapped EVA endpoint to use for the call
   * @param {IEvaServiceArguments} [args] Optional arguments for instantiating the service call
   */
  constructor(
    public data: SVC,
    public readonly endpoint: EvaEndpoint,
    args?: IEvaServiceArguments,
  ) {
    // Collect required settings
    //
    const coreSettings = getCoreSettings();
    const serviceSettings = getServiceSettings();

    if (coreSettings.appName === 'UNKNOWN') {
      throw new Error('Core setting missing: appName');
    }
    if (coreSettings.appVersion === '0.0.0') {
      throw new Error('Core setting missing: appVersion');
    }

    if (!this.data || !this.data.path) {
      throw new Error('Missing required EVA service url');
    }

    if (!this.endpoint || !this.endpoint.hasBootstrapped && (!args || !args.disableEndpointBootstrapCheck)) {
      throw new Error('Endpoint has not been bootstrapped');
    }

    // Extract the service name from the path
    //
    const urlSplit = `${this.data.path}`.split('/');
    const serviceName = urlSplit[urlSplit.length - 1];
    this.serviceName = serviceName;

    // Build the request options setting default headers
    // and parameters
    //
    this.defaultOptions = {
      fetch:                serviceSettings.fetch,
      method:               'POST',
      timeout:              serviceSettings.defaultTimeout,
      credentials:          'include',
      headers: {
        Accept:             'application/json',
        'Accept-Language':  coreSettings.language,
        'Content-type':     'application/json',
        ClientName:         coreSettings.clientName,
        ClientVersion:      coreSettings.clientVersion,
        'EVA-User-Agent':   `${coreSettings.appName}/${coreSettings.appVersion}`,
        'EVA-API-Version':  `${EVA_API_VERSION}`,
      },
      hooks: {
        beforeRequest: [
          ...((!args || !args.disableInterceptors) ? evaServiceInterceptors.getRequestInterceptors() : []),
        ],
        afterResponse: [
          (request, options, response) => {
            const requestUrl = new URL(request.url);
            const origin = requestUrl.origin;
            const serviceSettings = getServiceSettings();
            const applicationTokens = serviceSettings.applicationTokens || {};

            // Check for EVA-Warning headers and log them to the console
            // This used to be an interceptor/hook but is so important it is now part
            // of the core service class
            //
            response.headers.forEach((value, name) => {
              if (`${name}`.toLowerCase() === 'EVA-Warning'.toLowerCase()) {
                // EVA Warnings should always be logged so we will not use the logger
                // which can be disabled
                //
                console.warn(`[EVA:WarningLogger] ${value}`);
              }

              if (`${name}`.toLowerCase() === 'EVA-App-Token'.toLowerCase()) {
                // EVA Application tokens are usually returned on create order
                // calls to provide secure access to anonymous orders
                //
                console.log(`[EVA:AppToken] received new application token ${value} for origin ${origin}`);
                applicationTokens[origin] = value;
                setServiceSetting('applicationTokens', applicationTokens);
              }
            });
            // NOOP
          },
          async (request, options, response) => {
            // Check for the Error content to be present
            // Error is the new property for this this used to be called Exception
            //
            if (response.status < 400) {
              const responseBody = await safeGetResponseJSON(response);
              if (responseBody && responseBody.Error) {
                logger.warn(`[EVA:${serviceName}] Error found in response, rejecting service call`, responseBody.Error)();
                const errorResponse = new Response(
                  JSON.stringify(responseBody),
                  {
                    status: 500,
                    headers: response.headers,
                    statusText: 'Internal Server Error',
                  },
                );
                return errorResponse;
              }

              // For the login call there is another error scenario and that is when
              // the `Authentication` value is not EVA.Core.AuthenticationResults.Authenticated (2)
              //
              if (responseBody && responseBody.Authentication !== undefined && responseBody.Authentication !== EVA.Core.AuthenticationResults.Authenticated) {
                logger.warn(`[EVA:${serviceName}] Failed authentication, rejecting service call`, responseBody.Error)();
                const authFailedResponse = new Response(
                  JSON.stringify(responseBody),
                  {
                    status: 409,
                    headers: response.headers,
                    statusText: `Authentication failure: ${responseBody.Authentication}`,
                  },
                );
                return authFailedResponse;
              }
              // NOOP
            }
          },
          ...((!args || !args.disableInterceptors) ? evaServiceInterceptors.getResponseInterceptors() : []),
        ],
      },
    };
  }

  /**
   * Sets the request payload
   *
   * @param {SVC['request']} request
   */
  public setRequest(request: SVC['request']) {
    this.data.request = request;
  }

  /**
   * Convenience method to create a Fetch API request for manually calling a
   * service. This can be useful for SSR implementations.
   *
   * NOTE: Manual fetch calls will not log EVA warnings or check for content errors
   *
   * See: https://springtreesolutions.github.io/eva-sdk-repo/#/services/endpoint?id=eva-warning
   * See: https://springtreesolutions.github.io/eva-sdk-repo/#/services/endpoint?id=eva-response-error
   *
   * @param {IEvaServiceCallOptions} [options]
   */
  public buildFetchRequest(options?: IEvaServiceCallOptions) {
    // Prepare the call configuration and determine the service url
    //
    const { requestUrl, config } = this.prepareCallOptions(options);

    // Re-apply certain core/service setting in-case they have been updated
    //
    const coreSettings = getCoreSettings();
    const serviceSettings = getServiceSettings();

    this.defaultOptions.timeout = serviceSettings.defaultTimeout;
    (this.defaultOptions.headers as Record<string, string>)['Accept-Language'] = options?.language || coreSettings.language;

    // Merge the default options. The default ky instance does this for us
    //
    const mergedOptions = {
      ...this.defaultOptions,
      ...config,
    };

    // Build the headers with the correct typing and Headers object
    //
    const headers = new Headers();
    for (const [key, value] of Object.entries(this.defaultOptions.headers as Record<string, string>)) {
      // Set default if not specified in config
      //
      if (!config.headers || !(config.headers as Record<string, string>)[key]) {
        headers.append(key, value);
      }
    }
    for (const [key, value] of Object.entries(config.headers as Record<string, string>)) {
      headers.append(key, value);
    }

    const fetchRequest = new Request(
      requestUrl,
      {
        headers,
        body: mergedOptions.json ? JSON.stringify(config.json) : undefined,
        cache: mergedOptions.cache,
        credentials: mergedOptions.credentials,
        integrity: mergedOptions.integrity,
        keepalive: mergedOptions.keepalive,
        method: mergedOptions.method,
        mode: mergedOptions.mode,
        redirect: mergedOptions.redirect,
        referrer: mergedOptions.referrer,
        referrerPolicy: mergedOptions.referrerPolicy,
        window: mergedOptions.window,
      },
    );

    return fetchRequest;
  }

  /**
   * Call the EVA backend service with the current value of `this.data.request`
   * as the payload. The response will be set on `this.data.response` and the
   * full response will be available on `this.fullResponse`.
   * Unless otherwise configured in the constructor all active interceptors will
   * be used on the request and response as required.
   *
   * @returns {Promise<RES>}
   */
  public async call(options?: IEvaServiceCallOptions): Promise<SVC> {
    // Check if this service instance has already been used
    //
    if (this.abortController !== undefined) {
      throw new Error('EVA Service instance has already been used. Please create an instance per call');
    }

    // Prepare the call configuration and determine the service url
    //
    const { requestUrl, config } = this.prepareCallOptions(options);

    // Add the abort controller signal if supplied or create an abort controller
    //
    config.signal = options?.abortSignal;
    if (!config.signal) {
      try {
        this.abortController = new AbortController();
        config.signal = this.abortController.signal;
      } catch (error) {
        // No Abort controller available
      }
    }

    logger.debug(`[EVA:${this.serviceName}] Doing call`, requestUrl, config)();
    try {
      // Provide the signal for the abort controller to function
      //
      this.fetchResponse = await ky(requestUrl, config);
      const responseBody = await safeGetResponseJSON(this.fetchResponse);

      logger.debug(`[EVA:${this.serviceName}] Call response status`, this.fetchResponse.status)();
      logger.trace(`[EVA:${this.serviceName}] Call response headers`, JSON.stringify(this.fetchResponse.headers))();
      logger.trace(`[EVA:${this.serviceName}] Call response data`, responseBody)();

      // Store response payload
      //
      this.data.response = responseBody;

      // Resolve with typed response object
      //
      return this.data;
    } catch (error) {
      logger.error(`[EVA:${this.serviceName}] Call failed`, error)();
      throw error;
    }
  }

  private prepareCallOptions(options?: IEvaServiceCallOptions): { requestUrl: string, config: Options } {
    // Collect required settings
    //
    const serviceSettings = getServiceSettings();
    const applicationTokens = serviceSettings.applicationTokens || {};

    // Build the request url by combining the service path with the endpoint
    //
    const requestUrl = `${this.endpoint.endPointUri}${this.data.path}`;
    const requestOrigin = new URL(this.endpoint.endPointUri).origin;

    // Merge any provided headers with the default headers to build the final config
    //
    const headers: {[key: string]: string} = {
      ...this.defaultOptions.headers as {[key: string]: string},
      ...options?.headers,
    };
    const config: Options = {
      ...this.defaultOptions,
      headers,
      json: this.data.request,
    };

    // Override timeout if requested
    // Don't use falsy check to allow 0 values
    //
    if (options?.timeout !== undefined) {
      config.timeout = options.timeout;
    }

    // Add authentication token if supplied
    //
    if (options?.authenticationToken) {
      headers.Authorization = options.authenticationToken;
    }

    // Add elevation token if supplied
    //
    if (options?.elevationToken) {
      headers['EVA-Elevation-Token'] = options.elevationToken;
    }

    // Add a unique call identifier header which will be echoed by the EVA backend
    //
    headers['EVA-App-ContextID'] = (options && options.serviceContext) || v4();
    headers['EVA-Service-Name'] = this.data.name;
    headers['EVA-App-PayloadID'] = safeStableJSONHash(this.data.request as Record<string|number, any>);

    // Add chain context if provided
    //
    if (options?.chainContext) {
      headers['EVA-App-ContextChainID'] = options.chainContext;
    }

    // Add the application token if available
    //
    if (options?.applicationToken) {
      headers['EVA-App-Token'] = options.applicationToken;
    } else if (applicationTokens[requestOrigin]) {
      headers['EVA-App-Token'] = applicationTokens[requestOrigin];
    }

    // Add the currently used station ID if set
    //
    if (options?.currentStationID) {
      headers['EVA-StationID'] = `${options.currentStationID}`;
    } else if (serviceSettings.currentStationID) {
      headers['EVA-StationID'] = `${serviceSettings.currentStationID}`;
    }

    // Add the requested organization unit id
    //
    if (options?.requestedOrganizationUnitID) {
      headers['EVA-Requested-OrganizationUnitID'] = `${options.requestedOrganizationUnitID}`;
    } else if (serviceSettings.requestedOrganizationUnitID) {
      headers['EVA-Requested-OrganizationUnitID'] = `${serviceSettings.requestedOrganizationUnitID}`;
    }

    // Add the requested organization unit query
    //
    if (options?.requestedOrganizationUnitQuery) {
      headers['EVA-Requested-OrganizationUnit-Query'] = JSON.stringify(options.requestedOrganizationUnitQuery);
    } else if (serviceSettings.requestedOrganizationUnitQuery) {
      headers['EVA-Requested-OrganizationUnit-Query'] = JSON.stringify(serviceSettings.requestedOrganizationUnitQuery);
    }

    // Add the id mode option if set
    //
    if (options?.backendIdMode) {
      headers['EVA-IDs-Mode'] = `${options.backendIdMode}`;
    } else if (serviceSettings.backendIdMode) {
      headers['EVA-IDs-Mode'] = `${serviceSettings.backendIdMode}`;
    }

    // Add the id backend id option if set
    //
    if (options?.backendIdSystemID) {
      headers['EVA-IDs-BackendSystemID'] = `${options.backendIdSystemID}`;
    } else if (serviceSettings.backendIdSystemID) {
      headers['EVA-IDs-BackendSystemID'] = `${serviceSettings.backendIdSystemID}`;
    }

    return { config, requestUrl };
  }
}
